import { isAuthConfigured } from "./config.js";
import { getHostAuthSession, HostAuthError, isImpersonationActive, } from "./hostAuth.js";
import { getUserManager } from "./userManager.js";
// Refresh a host token this many seconds before it expires so an API call
// never goes out with an about-to-lapse bearer.
const HOST_TOKEN_REFRESH_BUFFER_SECS = 60;
const OIDC_SILENT_RENEW_TIMEOUT_MS = 12_000;
function nowSecs() {
    return Date.now() / 1000;
}
function hostTokenExpired(token) {
    return token.expiresAt !== null && nowSecs() >= token.expiresAt;
}
function hostTokenExpiring(token) {
    return token.expiresAt !== null && token.expiresAt - nowSecs() <= HOST_TOKEN_REFRESH_BUFFER_SECS;
}
async function freshHostToken(session) {
    const current = session.getToken();
    if (current && !hostTokenExpiring(current))
        return current.accessToken;
    try {
        const refreshed = await session.refresh("expiring");
        return refreshed.accessToken;
    }
    catch {
        return null;
    }
}
function silentRenewTimeout() {
    return new Error("OIDC silent renew timed out");
}
async function withTimeout(promise, timeoutMs) {
    let timeout;
    try {
        return await Promise.race([
            promise,
            new Promise((_, reject) => {
                timeout = setTimeout(() => reject(silentRenewTimeout()), timeoutMs);
            }),
        ]);
    }
    finally {
        if (timeout)
            clearTimeout(timeout);
    }
}
/**
 * Read the current access_token imperatively (outside React). Returns null
 * if unconfigured, signed out, or the session has expired. In host-auth mode
 * this reads the host-provided token rather than the OIDC UserManager.
 */
export async function getBearerToken() {
    const hostSession = getHostAuthSession();
    if (hostSession) {
        const token = hostSession.getToken();
        if (!token || hostTokenExpired(token))
            return null;
        return token.accessToken;
    }
    if (!isAuthConfigured())
        return null;
    const user = await getUserManager().getUser();
    if (!user || user.expired)
        return null;
    return user.access_token ?? null;
}
// Like getBearerToken, but renews first when the token is missing or near
// expiry, so a token that lapses while the user fills a form does not drop the
// Authorization header and 401 the next write. In host-auth mode the renewal is
// a handshake to the desktop host, not an OIDC silent iframe.
export async function getFreshBearerToken() {
    const hostSession = getHostAuthSession();
    if (hostSession)
        return freshHostToken(hostSession);
    if (!isAuthConfigured())
        return null;
    const manager = getUserManager();
    const user = await manager.getUser();
    if (user && !user.expired)
        return user.access_token ?? null;
    try {
        const renewed = await withTimeout(manager.signinSilent(), OIDC_SILENT_RENEW_TIMEOUT_MS);
        return renewed?.access_token ?? null;
    }
    catch {
        // Renewal failed (Keycloak session ended, network issue, etc.). Remove
        // the stale user so AuthProvider's userUnloaded handler sets React state
        // to signedOut, surfacing a sign-in prompt instead of leaving the app in
        // a zombie state where the UI shows signed-in but every API call 401s.
        void manager.removeUser();
        return null;
    }
}
function hostErrorReason(err) {
    return err instanceof HostAuthError ? err.reason : "refresh_failed";
}
/**
 * Recover from a 401 on an API call and return a token to retry with, or null
 * if recovery isn't possible.
 *
 * Host-auth mode: ask the desktop host for a fresh token (reason "401"). If that
 * rejects the session is unusable, so we report a terminal error, tearing the
 * provider down to hostError, and return null. This covers the case the host
 * never broadcasts an error of its own (e.g. a handshake timeout), which would
 * otherwise leave a zombie signed-in shell where every call 401s. OIDC mode:
 * there is nothing to retry with, so remove the stale user so AuthProvider flips
 * to signedOut, and return null (unchanged behaviour).
 */
export async function recoverAuthAfter401() {
    const hostSession = getHostAuthSession();
    if (hostSession) {
        // Impersonation: the one-time code is spent, so a refresh cannot
        // recover. Skip the doomed refresh attempt and go straight to the terminal
        // error, which renders the "session ended" screen. This catches any request
        // already in flight at the expiry boundary so it does not retry a dead
        // session or spin on 401s.
        if (isImpersonationActive()) {
            hostSession.reportError("refresh_failed");
            return null;
        }
        try {
            const refreshed = await hostSession.refresh("401");
            return refreshed.accessToken;
        }
        catch (err) {
            hostSession.reportError(hostErrorReason(err));
            return null;
        }
    }
    if (!isAuthConfigured())
        return null;
    await getUserManager()
        .removeUser()
        .catch(() => undefined);
    return null;
}
//# sourceMappingURL=token.js.map